// TvetTenant.js
// TVET overlay guard: CampusCloud pages whose tables do not exist on the TVET campus databases.
//
// Measured 2026-10-27 with _migration/tools/schema-probe against all nine campus databases
// (MariaDB 10.11.16): 17 tables / 62 columns that CampusCloud queries are absent on every campus,
// identically. See PHASE1-CHANGELOG.md §9. Five of those tables belong to features D1/D3 already
// excluded from the port (live classes, email events, assessment lock); the ones listed below belong
// to CampusCloud features the overlay never adopted, so a TVET user who opens them gets an empty
// page (the pages swallow the SQL error) instead of a usable feature.
//
// This is the overlay's own file: CampusCloud shared files stay byte-identical, which is why the
// wiring is only one import plus one call site in App.js and one in SideMenuShared.jsx.
//
// Deliberately NOT blocked, because the API catches the missing table and the page still works:
//   /studentprofile, /reviewapplication, /interview, /admission  (getsectionedfields -> application_form_*)

// TvetTenant:CampusKeys from appsettings.1tvet.json — the tenant keys the API accepts.
//
// 'dev' is not a campus: it is the key for the shared TVET development deployment, chosen because
// services/apiClient.js resolves the tenant from the first DNS label of the host
// (dev.donbosco1tvet.com -> 'dev'). The API refuses any Subdomain header outside its CampusKeys with
// "Unknown tenant for this deployment.", so leaving it out here (or in appsettings.1tvet.json)
// makes the dev site log in against a key the API rejects. The two lists are pinned together by
// the R-2 block in TvetTenant.test.js.
export const TVET_CAMPUS_KEYS = [
   'canlubang',
   'cebu-balamban',
   'cebu-punta',
   'mabalacat',
   'makati',
   'mati',
   'naga',
   'legaspi',
   'legazpi',
   'calauan',
   'dev',
];

// path -> the tables that make it unusable, and the query that proves it.
const UNAVAILABLE_FEATURES = [
   {
      path: '/soa',
      title: 'Statement of Account is not available for TVET campuses',
      tables: ['soa'],
      evidence: 'CPhService.GetSOA -> SELECT * FROM soa',
   },
   {
      path: '/studentfees',
      title: 'Fee assessment is not available for TVET campuses',
      tables: ['oa_schedule_of_fees'],
      evidence: 'CPhService.GetScheduleOfFees -> from oa_schedule_of_fees',
   },
   {
      path: '/classroomattendance',
      title: 'Classroom attendance is not available for TVET campuses',
      tables: ['classroom_attendance'],
      evidence: 'SchoolManagementService.UpsertClassroomAttendance -> classroom_attendance',
   },
   {
      path: '/classroomattendancerpt',
      title: 'Classroom attendance reports are not available for TVET campuses',
      tables: ['classroom_attendance'],
      evidence: 'SchoolManagementService.GetClassroomAttendance -> classroom_attendance',
   },
   {
      path: '/periodattendance',
      title: 'Period attendance is not available for TVET campuses',
      tables: ['period_attendance'],
      evidence: 'SchoolManagementService.UpsertPeriodAttendance -> period_attendance',
   },
   {
      path: '/periodattendancerpt',
      title: 'Period attendance reports are not available for TVET campuses',
      tables: ['period_attendance'],
      evidence: 'SchoolManagementService.GetPeriodAttendance -> period_attendance',
   },
   {
      path: '/brainee-resources',
      title: 'Lesson resources are not available for TVET campuses',
      tables: ['lesson_resources'],
      evidence: 'BraineeResourcesService -> from lesson_resources',
   },
   {
      path: '/appinfogroups',
      title: 'Form section builder is not available for TVET campuses',
      tables: ['application_form_group'],
      evidence: 'ManagementService.GetAppInfoGroupList -> application_form_group',
   },
   {
      path: '/appinfofields',
      title: 'Form field builder is not available for TVET campuses',
      tables: ['application_form_fields'],
      evidence: 'ManagementService.GetAppInfoFieldList -> application_form_fields',
   },
];

const SESSION_SUBDOMAIN_KEY = 'lms-subdomain';

/**
 * Menu entries are declared both with and without a leading slash (menuConfig.js uses
 * link('classroomattendancerpt', ...) in three places), so compare normalised paths.
 */
export const normalizeTenantPath = (path) => {
   if (typeof path !== 'string') {
      return '';
   }

   const withoutQuery = path.split('?')[0].split('#')[0].trim();

   if (withoutQuery === '') {
      return '/';
   }

   const withLeadingSlash = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;

   // Drop a trailing slash so '/soa/' and '/soa' match the same entry.
   return withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/')
      ? withLeadingSlash.slice(0, -1)
      : withLeadingSlash;
};

export const isTvetTenant = (subdomain) => {
   const tenant = subdomain === undefined || subdomain === null
      ? localStorage.getItem(SESSION_SUBDOMAIN_KEY)
      : subdomain;

   if (!tenant) {
      return false;
   }

   return TVET_CAMPUS_KEYS.includes(String(tenant).toLowerCase());
};

/**
 * Returns the unavailable-feature record for a path on a TVET tenant, or null when the page is
 * supported (or the tenant is not a TVET campus, in which case nothing is ever blocked).
 */
export const getUnavailableFeature = (pathname, subdomain) => {
   if (!isTvetTenant(subdomain)) {
      return null;
   }

   const target = normalizeTenantPath(pathname);

   return UNAVAILABLE_FEATURES.find((feature) => feature.path === target) || null;
};

const menuItemTarget = (item) => {
   if (!item || !item.label || !item.label.props) {
      return null;
   }

   return typeof item.label.props.to === 'string' ? item.label.props.to : null;
};

/**
 * Removes menu entries (and now-empty sub-menus) that point at a blocked path. Menu items are the
 * plain objects built by menuConfig.js, so a label is an <Link> whose `to` prop is the route.
 */
export const hideUnavailableMenuItems = (items, subdomain) => {
   if (!isTvetTenant(subdomain) || !Array.isArray(items)) {
      return items;
   }

   const prune = (list) => list
      .filter(Boolean)
      .map((item) => {
         if (!Array.isArray(item.children)) {
            return item;
         }

         const children = prune(item.children);

         // A sub-menu whose children were all removed has nothing left to open.
         return children.length === 0 ? null : { ...item, children };
      })
      .filter((item) => {
         if (!item) {
            return false;
         }

         const to = menuItemTarget(item);

         return to === null || getUnavailableFeature(to, subdomain) === null;
      });

   return prune(items);
};
